/**
 * Elite Batch 24 Part 2 — Marketing Ops Workflows (11-20)
 *
 * PRODUCTION STANDARD — Every workflow includes:
 * 1. Case stamping (node 2) — unique case_id, timestamps, workflow version
 * 2. Confidence-gated AI — aiNodeV2 with confidence_score + evidence output
 * 3. Confidence routing — confidenceGate splits auto vs human review
 * 4. Post-execution verification — verifyNode reads back from target system
 * 5. Audit logging — sheetsNode logs every case outcome
 *
 * CORRECT FIELD NAMES: conditionData, approvalGateData, delayAction, code
 * Webhook triggers have EMPTY inputFields.
 */

// ============ BASE HELPERS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}

// ============ ENHANCED V2 HELPERS ============

function aiNodeV2(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  const enhancedSystem =
    systemPrompt +
    " Also include in your JSON response: confidence_score (0-100 integer rating your certainty), evidence_summary (one sentence citing the key data points you relied on), and needs_human_review (boolean, true if you are uncertain or the case is unusual).";
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${enhancedSystem}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "choices[0].message.content.confidence_score",
            customName: "ai_confidence",
            type: "number",
          },
          {
            name: "choices[0].message.content.evidence_summary",
            customName: "ai_evidence",
            type: "string",
          },
          {
            name: "choices[0].message.content.needs_human_review",
            customName: "ai_needs_review",
            type: "string",
          },
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

function caseStampNode(
  id: string,
  label: string,
  y: number,
  workflowName: string,
  x = 80,
): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Stamp case with unique ID, timestamp, and workflow version
const ts = Date.now();
const rand = Math.random().toString(36).substring(2, 8);
output.case_id = 'case_' + ts + '_' + rand;
output.workflow_name = '${workflowName}';
output.workflow_version = '1.0';
output.started_at = new Date(ts).toISOString();
output.source_channel = variables.source_channel || 'webhook';
output.status = 'intake_complete';`,
    },
    x,
  );
}

function confidenceGate(
  id: string,
  label: string,
  y: number,
  threshold = "75",
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable: "ai_confidence",
          value: threshold,
          comparison_operator: "GREATER THAN",
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function conditionNode(
  id: string,
  label: string,
  y: number,
  variable: string,
  operator: string,
  value: string,
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable,
          value,
          comparison_operator: operator,
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function approvalNode(
  id: string,
  label: string,
  y: number,
  message: string,
  timeoutHours = 24,
  x = 80,
): any {
  return n(
    id,
    "approvalGate",
    label,
    y,
    {
      approvalGateData: {
        approvers: [],
        message,
        channel: "email",
        timeoutHours,
      },
    },
    x,
  );
}

function delayNode(
  id: string,
  label: string,
  y: number,
  time: number,
  timeFormat: string,
  x = 80,
): any {
  return n(
    id,
    "delayAction",
    label,
    y,
    {
      delayAction: { time, timeFormat },
    },
    x,
  );
}

function verifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  url: string,
  headers: Array<{ key: string; value: string }>,
  expectedVars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url,
        headers,
        body: [],
        variables: [
          ...expectedVars,
          { name: "status_code", customName: "verify_status", type: "number" },
        ],
      },
    },
    x,
  );
}

function exceptionNode(id: string, label: string, y: number, x = 80): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Exception handler: categorize, log, and route
const errorType = variables.error_message || variables.verify_status || 'unknown';
const isTransient = ['timeout', '429', '503', '502'].some(e => String(errorType).includes(e));
const retryCount = parseInt(variables.retry_count || '0');
output.exception_type = isTransient ? 'transient' : 'permanent';
output.should_retry = isTransient && retryCount < 3 ? 'yes' : 'no';
output.retry_count = String(retryCount + 1);
output.exception_summary = 'Error: ' + errorType + ' | Type: ' + output.exception_type + ' | Retry: ' + output.should_retry;
output.escalate_to_human = (!isTransient || retryCount >= 3) ? 'yes' : 'no';
output.logged_at = new Date().toISOString();`,
    },
    x,
  );
}

function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}

function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}

function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ PART 2 — MARKETING OPS (10 workflows) ============

export const ELITE_BATCH24_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 11. Elite: Blog to Social Repurposing
  // ===================================================================
  "Elite: Blog to Social Repurposing": {
    nodes: [
      trigger("Webhook: New blog post published on CMS", "webhook_app", {
        source: "WordPress",
        event: "post.published",
        howItStarts:
          "Fires when a new blog post is published on the CMS, triggering AI-powered social media content generation and scheduling across platforms",
        setup:
          "Configure your CMS (WordPress, Ghost, or Webflow) to send a webhook when a post transitions to 'published' status. Include post_url, title, excerpt, author, and categories in the payload.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for social repurposing",
        240,
        "Blog to Social Repurposing",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Extract key points + generate social variants",
        480,
        "You are a social media content strategist. Analyze the blog post content and generate platform-specific social media posts. Create engaging, platform-native content that drives clicks without being clickbait. Each variant should highlight a different angle or takeaway from the post.",
        "Generate social media posts from this blog: {{{1.body}}}. Return JSON with: post_title, post_url, post_category, linkedin_post (professional tone, 150-200 words with hook), twitter_post (concise, under 280 chars with hashtags), facebook_post (conversational, 80-120 words), instagram_caption (with emojis and hashtags, 100-150 words), key_quote (best pullquote from the article), cta_text (call-to-action phrase), suggested_image_prompt (for AI image generation).",
        [
          { field: "post_title", as: "post_title", type: "string" },
          { field: "post_url", as: "post_url", type: "string" },
          { field: "post_category", as: "post_category", type: "string" },
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          { field: "twitter_post", as: "twitter_post", type: "string" },
          { field: "facebook_post", as: "facebook_post", type: "string" },
          {
            field: "instagram_caption",
            as: "instagram_caption",
            type: "string",
          },
          { field: "key_quote", as: "key_quote", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
        ],
      ),

      n(
        "4",
        "hitApi",
        "WordPress: Fetch full post metadata + featured image",
        720,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://your-site.com/wp-json/wp/v2/posts?slug={post_url}&_embed",
            headers: [
              { key: "Authorization", value: "Bearer {wordpress_token}" },
            ],
            body: [],
            variables: [
              {
                name: "[0].title.rendered",
                customName: "cms_title",
                type: "string",
              },
              {
                name: "[0].excerpt.rendered",
                customName: "cms_excerpt",
                type: "string",
              },
              {
                name: "[0]._embedded.wp:featuredmedia[0].source_url",
                customName: "featured_image_url",
                type: "string",
              },
              {
                name: "[0].categories[0]",
                customName: "category_id",
                type: "string",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — schedule posts via Buffer
      n("6", "hitApi", "Buffer: Schedule social posts across platforms", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bufferapp.com/1/updates/create.json",
          headers: [
            { key: "Authorization", value: "Bearer {buffer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "text", value: "{linkedin_post}" },
            {
              key: "profile_ids",
              value:
                '["linkedin_profile_id","twitter_profile_id","facebook_profile_id"]',
            },
            {
              key: "media",
              value: '{"link":"{post_url}","photo":"{featured_image_url}"}',
            },
            { key: "scheduled_at", value: "" },
          ],
          variables: [
            { name: "success", customName: "buffer_success", type: "boolean" },
            {
              name: "updates[0].id",
              customName: "buffer_update_id",
              type: "string",
            },
          ],
        },
      }),

      // FALSE path — content lead reviews
      approvalNode(
        "7",
        "Approval: Content lead reviews AI-generated social posts",
        1200,
        "Social posts generated for blog: {post_title}. AI confidence: {ai_confidence}. LinkedIn: {linkedin_post}. Twitter: {twitter_post}. Evidence: {ai_evidence}. Please review for brand voice and accuracy before scheduling.",
        24,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm posts were scheduled in Buffer",
        1440,
        "GET",
        "https://api.bufferapp.com/1/updates/{buffer_update_id}.json",
        [{ key: "Authorization", value: "Bearer {buffer_token}" }],
        [
          { name: "status", customName: "post_status", type: "string" },
          {
            name: "scheduled_at",
            customName: "scheduled_time",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Posts scheduled successfully?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log social repurposing audit trail",
        1920,
        "SocialRepurposing",
        '[["{case_id}","{post_title}","{post_url}","{post_category}","{ai_confidence}","{buffer_update_id}","{scheduled_time}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Repurposes blog posts into platform-specific social content using AI, fetches CMS metadata, auto-schedules posts via Buffer for high-confidence content, routes uncertain posts for content lead review, verifies scheduling, and logs the audit trail.",
  },

  // ===================================================================
  // 12. Elite: Campaign Attribution Sync
  // ===================================================================
  "Elite: Campaign Attribution Sync": {
    nodes: [
      trigger("Schedule: Daily campaign attribution analysis", "schedule", {
        schedule: "Every day at 6:00 AM",
        howItStarts:
          "Runs daily to analyze marketing touchpoints across channels, map attribution to pipeline revenue, and sync to the analytics dashboard",
        setup:
          "Set up a daily scheduled trigger at 6 AM. Ensure HubSpot has campaign tracking enabled and UTM parameters are captured on all lead sources.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for attribution sync",
        240,
        "Campaign Attribution Sync",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Map touchpoints to multi-touch attribution model",
        480,
        "You are a marketing analytics expert specializing in multi-touch attribution. Analyze the touchpoint data to calculate first-touch, last-touch, and linear attribution credits for each campaign. Identify the highest-performing channels and flag any attribution gaps.",
        "Analyze touchpoint data for attribution: {{{1.body}}}. Return JSON with: analysis_date, total_touchpoints_analyzed, total_pipeline_attributed, top_channel (highest revenue attribution), top_campaign (best performing campaign), first_touch_leader (top first-touch channel), last_touch_leader (top last-touch channel), attribution_gaps (channels with missing data), anomalies_detected (unusual patterns), dashboard_summary (3-sentence executive overview), data_quality_score (0-100).",
        [
          { field: "analysis_date", as: "analysis_date", type: "string" },
          {
            field: "total_touchpoints_analyzed",
            as: "total_touchpoints",
            type: "string",
          },
          {
            field: "total_pipeline_attributed",
            as: "total_pipeline",
            type: "string",
          },
          { field: "top_channel", as: "top_channel", type: "string" },
          { field: "top_campaign", as: "top_campaign", type: "string" },
          { field: "attribution_gaps", as: "attribution_gaps", type: "string" },
          {
            field: "anomalies_detected",
            as: "anomalies_detected",
            type: "string",
          },
          {
            field: "dashboard_summary",
            as: "dashboard_summary",
            type: "string",
          },
          {
            field: "data_quality_score",
            as: "data_quality_score",
            type: "number",
          },
        ],
      ),

      n(
        "4",
        "hitApi",
        "HubSpot: Fetch pipeline deals with campaign source data",
        720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/deals/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"createdate","operator":"WITHIN","value":"1"}]}]',
              },
              {
                key: "properties",
                value:
                  '["dealname","amount","pipeline","hs_analytics_source","hs_analytics_source_data_1"]',
              },
            ],
            variables: [
              { name: "total", customName: "deals_analyzed", type: "number" },
              {
                name: "results[0].properties.hs_analytics_source",
                customName: "sample_source",
                type: "string",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — update dashboard
      sheetsNode(
        "6",
        "Google Sheets: Update attribution dashboard with latest data",
        1200,
        "Attribution",
        '[["{analysis_date}","{total_touchpoints}","{total_pipeline}","{top_channel}","{top_campaign}","{attribution_gaps}","{data_quality_score}","{dashboard_summary}"]]',
      ),

      // FALSE path — marketing ops review
      approvalNode(
        "7",
        "Approval: Marketing ops reviews attribution analysis",
        1200,
        "Daily attribution analysis completed but AI confidence is low ({ai_confidence}). Data quality score: {data_quality_score}. Gaps detected: {attribution_gaps}. Anomalies: {anomalies_detected}. Evidence: {ai_evidence}. Please review before dashboard update.",
        12,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm dashboard sheet was updated",
        1440,
        "GET",
        "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Attribution!A1:H1",
        [{ key: "Authorization", value: "Bearer {google_token}" }],
        [
          {
            name: "values[0][0]",
            customName: "last_entry_date",
            type: "string",
          },
          { name: "range", customName: "sheet_range", type: "string" },
        ],
      ),

      conditionNode(
        "9",
        "Check: Dashboard updated?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log attribution sync audit trail",
        1920,
        "AttributionAudit",
        '[["{case_id}","{analysis_date}","{deals_analyzed}","{total_pipeline}","{top_channel}","{data_quality_score}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Runs daily multi-touch attribution analysis using AI, pulls HubSpot pipeline data, auto-updates the attribution dashboard for high-confidence results, routes uncertain analyses for marketing ops review, verifies the update, and logs the audit trail.",
  },

  // ===================================================================
  // 13. Elite: Ad Spend Anomaly Detector
  // ===================================================================
  "Elite: Ad Spend Anomaly Detector": {
    nodes: [
      trigger("Schedule: Hourly ad spend monitoring", "schedule", {
        schedule: "Every hour",
        howItStarts:
          "Runs hourly to check ad spend across Google Ads and Facebook Ads for anomalies like overspend, underspend, unusual CPC spikes, or budget pacing issues",
        setup:
          "Set up an hourly scheduled trigger. Ensure Google Ads and Facebook Ads API credentials are configured with read access to campaign spend data.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for spend anomaly check",
        240,
        "Ad Spend Anomaly Detector",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Analyze spend data vs baselines for anomalies",
        480,
        "You are a paid media operations analyst specializing in anomaly detection. Compare current ad spend, CPC, CTR, and conversion rates against historical baselines. Flag any statistically significant deviations and recommend immediate actions.",
        "Analyze ad spend data: {{{1.body}}}. Return JSON with: check_timestamp, platform (google_ads/facebook/both), total_spend_today, daily_budget, pacing_percentage, cpc_current, cpc_baseline, cpc_deviation_pct, anomaly_type (overspend/underspend/cpc_spike/ctr_drop/conversion_drop/none), severity (critical/warning/info), root_cause_hypothesis (what might be causing the anomaly), recommended_action (specific next step), estimated_waste (dollar amount at risk if unaddressed).",
        [
          { field: "check_timestamp", as: "check_timestamp", type: "string" },
          { field: "platform", as: "platform", type: "string" },
          {
            field: "total_spend_today",
            as: "total_spend_today",
            type: "string",
          },
          { field: "daily_budget", as: "daily_budget", type: "string" },
          {
            field: "pacing_percentage",
            as: "pacing_percentage",
            type: "string",
          },
          { field: "anomaly_type", as: "anomaly_type", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          {
            field: "root_cause_hypothesis",
            as: "root_cause_hypothesis",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "estimated_waste", as: "estimated_waste", type: "string" },
        ],
      ),

      n(
        "4",
        "hitApi",
        "Google Ads: Fetch campaign spend + performance metrics",
        720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://googleads.googleapis.com/v14/customers/{customer_id}/googleAds:searchStream",
            headers: [
              { key: "Authorization", value: "Bearer {google_ads_token}" },
              { key: "developer-token", value: "{google_ads_dev_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "query",
                value:
                  "SELECT campaign.name, metrics.cost_micros, metrics.clicks, metrics.impressions, metrics.average_cpc FROM campaign WHERE segments.date = TODAY",
              },
            ],
            variables: [
              {
                name: "results[0].metrics.cost_micros",
                customName: "google_spend_micros",
                type: "number",
              },
              {
                name: "results[0].metrics.average_cpc",
                customName: "google_avg_cpc",
                type: "number",
              },
              {
                name: "results[0].metrics.clicks",
                customName: "google_clicks",
                type: "number",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — urgent Slack alert
      slackNode(
        "6",
        "Slack: Send urgent ad spend anomaly alert",
        1200,
        ":rotating_light: *AD SPEND ANOMALY DETECTED*\n*Platform:* {platform} | *Severity:* {severity}\n*Anomaly:* {anomaly_type}\n*Spend Today:* ${total_spend_today} / ${daily_budget} budget ({pacing_percentage}% pacing)\n*Root Cause:* {root_cause_hypothesis}\n*Estimated Waste:* ${estimated_waste}\n*Recommended Action:* {recommended_action}\n*AI Confidence:* {ai_confidence}% | *Case:* {case_id}",
      ),

      // FALSE path — media buyer review
      approvalNode(
        "7",
        "Approval: Media buyer reviews uncertain anomaly alert",
        1200,
        "Potential ad spend anomaly detected on {platform} but AI confidence is low ({ai_confidence}). Type: {anomaly_type}, Severity: {severity}. Spend: ${total_spend_today}/${daily_budget}. Hypothesis: {root_cause_hypothesis}. Evidence: {ai_evidence}. Please investigate and confirm or dismiss.",
        4,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm alert was delivered to Slack",
        1440,
        "GET",
        "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        [{ key: "Content-Type", value: "application/json" }],
        [{ name: "ok", customName: "alert_confirmed", type: "boolean" }],
      ),

      conditionNode(
        "9",
        "Check: Alert sent successfully?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log ad spend anomaly audit trail",
        1920,
        "AdSpendAnomalies",
        '[["{case_id}","{check_timestamp}","{platform}","{anomaly_type}","{severity}","{total_spend_today}","{daily_budget}","{estimated_waste}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Monitors ad spend hourly using AI to detect anomalies against baselines, pulls live Google Ads metrics, sends urgent Slack alerts for high-confidence anomalies, routes uncertain flags for media buyer review, verifies alert delivery, and logs the audit trail.",
  },

  // ===================================================================
  // 14. Elite: SEO Content Gap Finder
  // ===================================================================
  "Elite: SEO Content Gap Finder": {
    nodes: [
      trigger("Schedule: Weekly SEO content gap analysis", "schedule", {
        schedule: "Every Monday at 7:00 AM",
        howItStarts:
          "Runs weekly to analyze keyword rankings, identify content gaps versus competitors, and create prioritized content briefs as Asana tasks",
        setup:
          "Set up a weekly Monday 7 AM trigger. Configure Ahrefs API access with your domain and top 3 competitor domains for gap analysis.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for SEO gap analysis",
        240,
        "SEO Content Gap Finder",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Analyze rankings + identify high-value content gaps",
        480,
        "You are an SEO strategist. Analyze the keyword ranking data and competitor content to identify high-value content gaps — keywords where competitors rank but we do not. Prioritize by search volume, keyword difficulty, and business relevance. Generate actionable content briefs.",
        "Analyze SEO data: {{{1.body}}}. Return JSON with: analysis_date, total_keywords_analyzed, gaps_found, top_gap_keyword (highest opportunity keyword), top_gap_volume (monthly search volume), top_gap_difficulty (0-100), content_brief_title (suggested article title), content_brief_outline (5 key sections), target_word_count, estimated_traffic_potential, priority_level (P1/P2/P3), competitor_ranking_for_gap (which competitor ranks and at what position).",
        [
          { field: "analysis_date", as: "analysis_date", type: "string" },
          { field: "gaps_found", as: "gaps_found", type: "number" },
          { field: "top_gap_keyword", as: "top_gap_keyword", type: "string" },
          { field: "top_gap_volume", as: "top_gap_volume", type: "string" },
          {
            field: "top_gap_difficulty",
            as: "top_gap_difficulty",
            type: "string",
          },
          {
            field: "content_brief_title",
            as: "content_brief_title",
            type: "string",
          },
          {
            field: "content_brief_outline",
            as: "content_brief_outline",
            type: "string",
          },
          {
            field: "target_word_count",
            as: "target_word_count",
            type: "string",
          },
          {
            field: "estimated_traffic_potential",
            as: "estimated_traffic_potential",
            type: "string",
          },
          { field: "priority_level", as: "priority_level", type: "string" },
        ],
      ),

      n(
        "4",
        "hitApi",
        "Ahrefs: Fetch keyword rankings + competitor gap data",
        720,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.ahrefs.com/v3/site-explorer/organic-keywords?target={domain}&limit=100&mode=domain",
            headers: [{ key: "Authorization", value: "Bearer {ahrefs_token}" }],
            body: [],
            variables: [
              {
                name: "keywords[0].keyword",
                customName: "top_keyword",
                type: "string",
              },
              {
                name: "keywords[0].position",
                customName: "top_position",
                type: "number",
              },
              {
                name: "keywords[0].volume",
                customName: "top_volume",
                type: "number",
              },
              { name: "total", customName: "total_rankings", type: "number" },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — create Asana content tasks
      n(
        "6",
        "hitApi",
        "Asana: Create content brief task with SEO details",
        1200,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://app.asana.com/api/1.0/tasks",
            headers: [
              { key: "Authorization", value: "Bearer {asana_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "data",
                value:
                  '{"name":"[{priority_level}] SEO Content: {content_brief_title}","notes":"KEYWORD: {top_gap_keyword}\\nVOLUME: {top_gap_volume}/mo | DIFFICULTY: {top_gap_difficulty}\\nTRAFFIC POTENTIAL: {estimated_traffic_potential}\\n\\nOUTLINE:\\n{content_brief_outline}\\n\\nTARGET WORD COUNT: {target_word_count}\\nCASE: {case_id}","projects":["{asana_content_project_id}"]}',
              },
            ],
            variables: [
              { name: "data.gid", customName: "task_gid", type: "string" },
              { name: "data.name", customName: "task_name", type: "string" },
            ],
          },
        },
      ),

      // FALSE path — SEO lead reviews
      approvalNode(
        "7",
        "Approval: SEO lead reviews content gap findings",
        1200,
        "SEO gap analysis found {gaps_found} opportunities. Top gap: '{top_gap_keyword}' (vol: {top_gap_volume}, difficulty: {top_gap_difficulty}). AI confidence: {ai_confidence}. Brief: {content_brief_title}. Evidence: {ai_evidence}. Please validate before creating content tasks.",
        24,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm Asana task was created",
        1440,
        "GET",
        "https://app.asana.com/api/1.0/tasks/{task_gid}",
        [{ key: "Authorization", value: "Bearer {asana_token}" }],
        [
          {
            name: "data.name",
            customName: "verified_task_name",
            type: "string",
          },
          { name: "data.gid", customName: "verified_task_gid", type: "string" },
        ],
      ),

      conditionNode(
        "9",
        "Check: Task created successfully?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log SEO gap analysis audit trail",
        1920,
        "SEOGaps",
        '[["{case_id}","{analysis_date}","{gaps_found}","{top_gap_keyword}","{top_gap_volume}","{top_gap_difficulty}","{priority_level}","{ai_confidence}","{task_gid}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Performs weekly SEO content gap analysis using AI, pulls Ahrefs ranking data, auto-creates prioritized Asana content tasks for high-confidence gaps, routes uncertain findings for SEO lead review, verifies task creation, and logs the audit trail.",
  },

  // ===================================================================
  // 15. Elite: Competitor Mention Tracker
  // ===================================================================
  "Elite: Competitor Mention Tracker": {
    nodes: [
      trigger(
        "Webhook: Brand monitoring alert for competitor mention",
        "webhook_app",
        {
          source: "Mention",
          event: "alert.triggered",
          howItStarts:
            "Fires when a competitor is mentioned in a context relevant to your brand, such as comparison articles, review sites, social media, or forum discussions",
          setup:
            "Configure Mention.com (or Brandwatch/Brand24) to send webhook alerts when competitor names appear alongside your brand or in your market segment. Include source_url, text, author, platform, and sentiment.",
          inputFields: [],
        },
      ),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for competitor mention",
        240,
        "Competitor Mention Tracker",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Classify mention sentiment + assess response need",
        480,
        "You are a competitive intelligence analyst. Classify the competitor mention by sentiment, context, and strategic relevance. Determine if the mention requires a response (e.g., correcting misinformation, joining a conversation, or updating battlecards) and recommend the appropriate action.",
        "Analyze this competitor mention: {{{1.body}}}. Return JSON with: source_url, platform (social/forum/review/news/blog), competitor_name, mention_context (comparison/review/complaint/praise/question), sentiment (positive/neutral/negative toward us), strategic_relevance (high/medium/low), requires_response (boolean), response_type (comment/article/battlecard_update/none), suggested_response (draft response if needed), competitive_insight (key takeaway for the team).",
        [
          { field: "source_url", as: "source_url", type: "string" },
          { field: "platform", as: "platform", type: "string" },
          { field: "competitor_name", as: "competitor_name", type: "string" },
          { field: "mention_context", as: "mention_context", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
          {
            field: "strategic_relevance",
            as: "strategic_relevance",
            type: "string",
          },
          {
            field: "requires_response",
            as: "requires_response",
            type: "string",
          },
          { field: "response_type", as: "response_type", type: "string" },
          {
            field: "suggested_response",
            as: "suggested_response",
            type: "string",
          },
          {
            field: "competitive_insight",
            as: "competitive_insight",
            type: "string",
          },
        ],
      ),

      n(
        "4",
        "hitApi",
        "Mention: Fetch full alert details + historical context",
        720,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.mention.net/api/accounts/{account_id}/alerts/{alert_id}/mentions",
            headers: [
              { key: "Authorization", value: "Bearer {mention_token}" },
            ],
            body: [],
            variables: [
              {
                name: "mentions[0].title",
                customName: "mention_title",
                type: "string",
              },
              {
                name: "mentions[0].original_url",
                customName: "original_url",
                type: "string",
              },
              {
                name: "mentions[0].source_name",
                customName: "source_name",
                type: "string",
              },
              {
                name: "meta.count",
                customName: "total_mentions",
                type: "number",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — Slack alert + log
      slackNode(
        "6",
        "Slack: Alert team with competitor intelligence brief",
        1200,
        ":mag: *Competitor Mention Detected*\n*Competitor:* {competitor_name} | *Platform:* {platform}\n*Context:* {mention_context} | *Sentiment:* {sentiment}\n*Relevance:* {strategic_relevance} | *Response Needed:* {requires_response}\n*Source:* {source_url}\n*Insight:* {competitive_insight}\n*Suggested Response:* {suggested_response}\n*AI Confidence:* {ai_confidence}% | *Case:* {case_id}",
      ),

      // FALSE path — PR team review
      approvalNode(
        "7",
        "Approval: PR team reviews uncertain competitor mention",
        1200,
        "Competitor mention flagged on {platform} about {competitor_name}. Sentiment toward us: {sentiment}. Context: {mention_context}. AI confidence is low ({ai_confidence}). Evidence: {ai_evidence}. Source: {source_url}. Please review and determine if response is needed.",
        12,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm competitive intel was logged",
        1440,
        "GET",
        "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/CompetitorIntel!A1:J1",
        [{ key: "Authorization", value: "Bearer {google_token}" }],
        [
          { name: "values", customName: "logged_data", type: "string" },
          { name: "range", customName: "log_range", type: "string" },
        ],
      ),

      conditionNode(
        "9",
        "Check: Intel logged successfully?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log competitor mention audit trail",
        1920,
        "CompetitorIntel",
        '[["{case_id}","{competitor_name}","{platform}","{mention_context}","{sentiment}","{strategic_relevance}","{requires_response}","{competitive_insight}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Tracks competitor mentions using AI to classify sentiment and strategic relevance, auto-alerts the team via Slack for high-confidence insights, routes uncertain mentions for PR team review, verifies intel was logged, and maintains the complete audit trail.",
  },

  // ===================================================================
  // 16. Elite: Webinar Attendee Follow-Up
  // ===================================================================
  "Elite: Webinar Attendee Follow-Up": {
    nodes: [
      trigger(
        "Webhook: Webinar ended — attendee data available",
        "webhook_app",
        {
          source: "Zoom",
          event: "webinar.ended",
          howItStarts:
            "Fires when a webinar ends in Zoom, triggering attendee engagement analysis and personalized follow-up email sequences based on participation level",
          setup:
            "Configure Zoom webhook for webinar.ended event. Ensure webinar registration includes email, name, and company. Attendee engagement metrics (poll responses, Q&A, duration) are included automatically.",
          inputFields: [],
        },
      ),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for webinar follow-up",
        240,
        "Webinar Attendee Follow-Up",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Classify attendee engagement + personalize follow-up",
        480,
        "You are a demand generation specialist. Analyze webinar attendee data to classify engagement level (based on attendance duration, poll participation, Q&A activity). Generate a personalized follow-up email that references specific moments from the webinar they engaged with.",
        "Analyze webinar attendee data: {{{1.body}}}. Return JSON with: attendee_email, attendee_name, company_name, attendance_duration_pct, engagement_tier (highly_engaged/engaged/passive/no_show), poll_participation (boolean), asked_question (boolean), engagement_score (0-100), follow_up_subject (personalized subject line), follow_up_body (personalized email referencing their engagement), cta_type (demo_request/content_download/community_join), lead_priority (hot/warm/cold).",
        [
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "engagement_tier", as: "engagement_tier", type: "string" },
          { field: "engagement_score", as: "engagement_score", type: "number" },
          {
            field: "follow_up_subject",
            as: "follow_up_subject",
            type: "string",
          },
          { field: "follow_up_body", as: "follow_up_body", type: "string" },
          { field: "cta_type", as: "cta_type", type: "string" },
          { field: "lead_priority", as: "lead_priority", type: "string" },
        ],
      ),

      n("4", "hitApi", "HubSpot: Lookup attendee in CRM for context", 720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{attendee_email}"}]}]',
            },
          ],
          variables: [
            { name: "total", customName: "contact_exists", type: "number" },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
            {
              name: "results[0].properties.hs_object_id",
              customName: "contact_id",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — send personalized follow-up
      emailNode(
        "6",
        "SendGrid: Send personalized webinar follow-up email",
        1200,
        "{attendee_email}",
        "{follow_up_subject}",
        "{follow_up_body}\n\n---\nEngagement Score: {engagement_score}/100 | Tier: {engagement_tier}\nCase: {case_id}",
      ),

      // FALSE path — marketing review
      approvalNode(
        "7",
        "Approval: Marketing reviews follow-up personalization",
        1200,
        "Webinar follow-up for {attendee_name} at {company_name} needs review. Engagement tier: {engagement_tier} (score: {engagement_score}). AI confidence: {ai_confidence}. Follow-up subject: {follow_up_subject}. Evidence: {ai_evidence}. Please approve or edit the personalized message.",
        12,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm follow-up email was sent",
        1440,
        "GET",
        "https://api.sendgrid.com/v3/messages?query=to_email%3D{attendee_email}&limit=1",
        [{ key: "Authorization", value: "Bearer {sendgrid_key}" }],
        [
          {
            name: "messages[0].status",
            customName: "email_delivery_status",
            type: "string",
          },
          {
            name: "messages[0].msg_id",
            customName: "email_msg_id",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Email sent successfully?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log webinar follow-up audit trail",
        1920,
        "WebinarFollowUp",
        '[["{case_id}","{attendee_email}","{company_name}","{engagement_tier}","{engagement_score}","{cta_type}","{lead_priority}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Analyzes webinar attendee engagement with AI, looks up CRM context, auto-sends personalized follow-up emails for high-confidence classifications, routes uncertain ones for marketing review, verifies delivery, and logs the audit trail.",
  },

  // ===================================================================
  // 17. Elite: Lead Magnet Delivery + Nurture
  // ===================================================================
  "Elite: Lead Magnet Delivery + Nurture": {
    nodes: [
      trigger("Webhook: Lead magnet download form submitted", "webhook_form", {
        source: "Website",
        event: "form.lead_magnet",
        howItStarts:
          "Fires when a prospect submits a form to download a gated content asset (ebook, whitepaper, template), triggering asset delivery, CRM entry, and nurture sequence setup",
        setup:
          "Configure your lead magnet landing page form to POST submissions to this webhook. Include email, name, company, and the asset_id or asset_name requested.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for lead magnet delivery",
        240,
        "Lead Magnet Delivery + Nurture",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Extract contact details + match to correct asset",
        480,
        "You are a demand generation operations specialist. Parse the form submission to extract contact details, identify the requested asset, and determine the appropriate nurture sequence based on the content topic, the prospect's likely buying stage, and their company profile.",
        "Process this lead magnet request: {{{1.body}}}. Return JSON with: contact_email, contact_name, company_name, requested_asset (exact asset name), asset_download_url (construct from asset library), topic_category (category of the content), buying_stage (awareness/consideration/decision), nurture_sequence (which email sequence to enroll them in), lead_score_adjustment (points to add for this activity), delivery_subject (email subject for asset delivery), delivery_body (personalized delivery email with asset link and next-step CTA).",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "requested_asset", as: "requested_asset", type: "string" },
          {
            field: "asset_download_url",
            as: "asset_download_url",
            type: "string",
          },
          { field: "topic_category", as: "topic_category", type: "string" },
          { field: "buying_stage", as: "buying_stage", type: "string" },
          { field: "nurture_sequence", as: "nurture_sequence", type: "string" },
          { field: "delivery_subject", as: "delivery_subject", type: "string" },
          { field: "delivery_body", as: "delivery_body", type: "string" },
        ],
      ),

      n(
        "4",
        "hitApi",
        "HubSpot: Create or update contact with asset download",
        720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{contact_email}","firstname":"{contact_name}","company":"{company_name}","last_content_downloaded":"{requested_asset}","content_topic":"{topic_category}","lifecycle_stage":"lead"}',
              },
            ],
            variables: [
              { name: "id", customName: "contact_id", type: "string" },
              {
                name: "properties.email",
                customName: "created_email",
                type: "string",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — deliver asset via email
      emailNode(
        "6",
        "SendGrid: Deliver lead magnet asset via email",
        1200,
        "{contact_email}",
        "{delivery_subject}",
        "{delivery_body}",
      ),

      // FALSE path — marketing review
      approvalNode(
        "7",
        "Approval: Marketing reviews asset match + delivery",
        1200,
        "Lead magnet request from {contact_name} at {company_name}. Requested: {requested_asset}. Matched asset URL: {asset_download_url}. AI confidence: {ai_confidence}. Buying stage: {buying_stage}. Evidence: {ai_evidence}. Please verify asset match before delivery.",
        8,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm delivery email was sent",
        1440,
        "GET",
        "https://api.sendgrid.com/v3/messages?query=to_email%3D{contact_email}&limit=1",
        [{ key: "Authorization", value: "Bearer {sendgrid_key}" }],
        [
          {
            name: "messages[0].status",
            customName: "delivery_status",
            type: "string",
          },
          {
            name: "messages[0].msg_id",
            customName: "delivery_msg_id",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Asset delivered successfully?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log lead magnet delivery audit trail",
        1920,
        "LeadMagnets",
        '[["{case_id}","{contact_email}","{company_name}","{requested_asset}","{topic_category}","{buying_stage}","{nurture_sequence}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Processes lead magnet downloads using AI to match assets and classify buying stage, creates CRM contacts, auto-delivers assets via email for high-confidence matches, routes uncertain ones for marketing review, verifies delivery, and logs the audit trail.",
  },

  // ===================================================================
  // 18. Elite: UTM Governance Validator
  // ===================================================================
  "Elite: UTM Governance Validator": {
    nodes: [
      trigger("Schedule: Daily UTM parameter audit", "schedule", {
        schedule: "Every day at 5:00 AM",
        howItStarts:
          "Runs daily to scan recent leads for non-compliant UTM parameters, auto-corrects standardization issues, and flags major violations for marketing ops review",
        setup:
          "Set up a daily 5 AM trigger. Ensure HubSpot captures UTM parameters (utm_source, utm_medium, utm_campaign) on all form submissions and landing pages.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for UTM audit",
        240,
        "UTM Governance Validator",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Validate UTM patterns against naming conventions",
        480,
        "You are a marketing operations governance specialist. Analyze the UTM parameters from recent leads against the standard naming conventions. Identify violations (wrong casing, non-standard source names, missing required parameters), suggest corrections, and categorize severity.",
        "Validate UTM parameters: {{{1.body}}}. Return JSON with: audit_date, leads_scanned, violations_found, violation_types (comma-separated: wrong_case/non_standard_source/missing_medium/invalid_campaign_format), most_common_violation, auto_fixable_count, manual_review_count, corrected_utms (JSON object mapping original to corrected values), worst_offending_campaign (campaign with most violations), compliance_rate (percentage of compliant UTMs), recommendation (summary of systemic issues to fix at source).",
        [
          { field: "audit_date", as: "audit_date", type: "string" },
          { field: "leads_scanned", as: "leads_scanned", type: "string" },
          { field: "violations_found", as: "violations_found", type: "number" },
          { field: "violation_types", as: "violation_types", type: "string" },
          {
            field: "most_common_violation",
            as: "most_common_violation",
            type: "string",
          },
          {
            field: "auto_fixable_count",
            as: "auto_fixable_count",
            type: "string",
          },
          {
            field: "manual_review_count",
            as: "manual_review_count",
            type: "string",
          },
          { field: "compliance_rate", as: "compliance_rate", type: "string" },
          {
            field: "worst_offending_campaign",
            as: "worst_offending_campaign",
            type: "string",
          },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),

      n("4", "hitApi", "HubSpot: Fetch recent leads with UTM properties", 720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"createdate","operator":"WITHIN","value":"1"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","utm_source","utm_medium","utm_campaign","utm_content","utm_term"]',
            },
          ],
          variables: [
            { name: "total", customName: "total_recent_leads", type: "number" },
            {
              name: "results[0].properties.utm_source",
              customName: "sample_utm_source",
              type: "string",
            },
            {
              name: "results[0].properties.utm_campaign",
              customName: "sample_utm_campaign",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — auto-fix + log corrections
      sheetsNode(
        "6",
        "Google Sheets: Log auto-corrected UTMs to governance dashboard",
        1200,
        "UTMGovernance",
        '[["{audit_date}","{leads_scanned}","{violations_found}","{auto_fixable_count}","{compliance_rate}","{most_common_violation}","{worst_offending_campaign}","{recommendation}"]]',
      ),

      // FALSE path — marketing ops review
      approvalNode(
        "7",
        "Approval: Marketing ops reviews UTM violations",
        1200,
        "UTM audit found {violations_found} violations across {leads_scanned} leads. Compliance rate: {compliance_rate}. Most common: {most_common_violation}. Worst campaign: {worst_offending_campaign}. AI confidence: {ai_confidence}. Manual review needed for {manual_review_count} entries. Evidence: {ai_evidence}.",
        24,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm governance log was updated",
        1440,
        "GET",
        "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/UTMGovernance!A1:H1",
        [{ key: "Authorization", value: "Bearer {google_token}" }],
        [
          {
            name: "values[0][0]",
            customName: "last_audit_date",
            type: "string",
          },
          { name: "range", customName: "sheet_range", type: "string" },
        ],
      ),

      conditionNode(
        "9",
        "Check: Governance log updated?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log UTM audit execution trail",
        1920,
        "UTMAuditTrail",
        '[["{case_id}","{audit_date}","{leads_scanned}","{violations_found}","{compliance_rate}","{auto_fixable_count}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Audits UTM parameters daily using AI to validate naming conventions, pulls recent HubSpot leads, auto-logs corrections for high-confidence fixes, routes complex violations for marketing ops review, verifies the governance dashboard update, and logs the audit trail.",
  },

  // ===================================================================
  // 19. Elite: Review Monitoring + Response
  // ===================================================================
  "Elite: Review Monitoring + Response": {
    nodes: [
      trigger("Webhook: New review posted on G2 or Trustpilot", "webhook_app", {
        source: "G2",
        event: "review.published",
        howItStarts:
          "Fires when a new review is posted on G2, Trustpilot, or Capterra, triggering AI sentiment analysis, response drafting, and CRM correlation",
        setup:
          "Configure G2/Trustpilot webhook integration to send review notifications. Include reviewer details, star rating, review text, platform, and product category.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for review response",
        240,
        "Review Monitoring + Response",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Classify sentiment + draft professional response",
        480,
        "You are a brand reputation manager. Analyze the review to classify sentiment, identify specific praise or complaints, and draft an appropriate public response. Responses should be professional, empathetic, and address specific points raised. For negative reviews, acknowledge concerns and offer resolution paths.",
        "Analyze this review: {{{1.body}}}. Return JSON with: reviewer_name, review_platform (g2/trustpilot/capterra), star_rating, overall_sentiment (positive/mixed/negative), key_praise (what they liked), key_complaints (what they disliked), product_areas_mentioned (features/support/pricing/ux), urgency (immediate/standard/low), response_draft (professional public reply addressing their specific points), internal_action_items (what the team should do internally), reviewer_email (if available for direct follow-up).",
        [
          { field: "reviewer_name", as: "reviewer_name", type: "string" },
          { field: "review_platform", as: "review_platform", type: "string" },
          { field: "star_rating", as: "star_rating", type: "string" },
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "string",
          },
          { field: "key_praise", as: "key_praise", type: "string" },
          { field: "key_complaints", as: "key_complaints", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "response_draft", as: "response_draft", type: "string" },
          {
            field: "internal_action_items",
            as: "internal_action_items",
            type: "string",
          },
          { field: "reviewer_email", as: "reviewer_email", type: "string" },
        ],
      ),

      n("4", "hitApi", "HubSpot: Correlate reviewer with CRM contact", 720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{reviewer_email}"}]}]',
            },
          ],
          variables: [
            { name: "total", customName: "reviewer_in_crm", type: "number" },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "reviewer_lifecycle",
              type: "string",
            },
            {
              name: "results[0].properties.hs_object_id",
              customName: "reviewer_contact_id",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — post response
      n(
        "6",
        "hitApi",
        "G2/Trustpilot: Post AI-drafted response to review",
        1200,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.g2.com/v1/reviews/{review_id}/responses",
            headers: [
              { key: "Authorization", value: "Bearer {g2_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "response", value: '{"body":"{response_draft}"}' }],
            variables: [
              { name: "id", customName: "response_id", type: "string" },
              { name: "status", customName: "response_status", type: "string" },
            ],
          },
        },
      ),

      // FALSE path — brand manager review
      approvalNode(
        "7",
        "Approval: Brand manager reviews response before posting",
        1200,
        "New {star_rating}-star review on {review_platform} from {reviewer_name}. Sentiment: {overall_sentiment}. Praise: {key_praise}. Complaints: {key_complaints}. AI confidence: {ai_confidence}. Draft response: {response_draft}. Please approve, edit, or reject before posting.",
        12,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm response was posted successfully",
        1440,
        "GET",
        "https://api.g2.com/v1/reviews/{review_id}/responses/{response_id}",
        [{ key: "Authorization", value: "Bearer {g2_token}" }],
        [
          {
            name: "status",
            customName: "verified_response_status",
            type: "string",
          },
          {
            name: "body",
            customName: "verified_response_body",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Response posted successfully?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log review monitoring audit trail",
        1920,
        "ReviewMonitoring",
        '[["{case_id}","{review_platform}","{reviewer_name}","{star_rating}","{overall_sentiment}","{urgency}","{key_complaints}","{ai_confidence}","{response_id}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Monitors reviews on G2/Trustpilot with AI sentiment analysis, correlates reviewers to CRM contacts, auto-posts professional responses for high-confidence cases, routes sensitive reviews for brand manager approval, verifies posting, and logs the audit trail.",
  },

  // ===================================================================
  // 20. Elite: Weekly KPI Digest Generator
  // ===================================================================
  "Elite: Weekly KPI Digest Generator": {
    nodes: [
      trigger("Schedule: Weekly KPI digest generation", "schedule", {
        schedule: "Every Friday at 4:00 PM",
        howItStarts:
          "Runs every Friday afternoon to compile marketing KPIs from multiple sources, generate an AI-powered narrative analysis, and email the digest to leadership",
        setup:
          "Set up a Friday 4 PM trigger. Ensure API access to Google Analytics, HubSpot, and ad platforms for metrics retrieval.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp Case: Generate case_id for KPI digest",
        240,
        "Weekly KPI Digest Generator",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Analyze metrics + generate executive narrative",
        480,
        "You are a marketing analytics director. Analyze the weekly KPI data across channels to generate an executive-ready narrative. Highlight wins, call out concerns, compare against targets, and provide strategic recommendations. The narrative should be data-driven but accessible to non-technical leadership.",
        "Generate weekly KPI digest from: {{{1.body}}}. Return JSON with: week_ending, total_leads_generated, leads_vs_target_pct, total_pipeline_created, pipeline_vs_target_pct, total_ad_spend, cost_per_lead, top_performing_channel, underperforming_channel, website_traffic, traffic_change_pct, executive_summary (5-sentence narrative with key highlights and concerns), strategic_recommendations (3 actionable items for next week), risk_flags (any metrics trending in wrong direction).",
        [
          { field: "week_ending", as: "week_ending", type: "string" },
          { field: "total_leads_generated", as: "total_leads", type: "string" },
          {
            field: "leads_vs_target_pct",
            as: "leads_vs_target",
            type: "string",
          },
          {
            field: "total_pipeline_created",
            as: "total_pipeline",
            type: "string",
          },
          { field: "total_ad_spend", as: "total_ad_spend", type: "string" },
          { field: "cost_per_lead", as: "cost_per_lead", type: "string" },
          {
            field: "top_performing_channel",
            as: "top_channel",
            type: "string",
          },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          {
            field: "strategic_recommendations",
            as: "strategic_recommendations",
            type: "string",
          },
          { field: "risk_flags", as: "risk_flags", type: "string" },
        ],
      ),

      n(
        "4",
        "hitApi",
        "Google Analytics + HubSpot: Fetch weekly metrics",
        720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
            headers: [
              {
                key: "Authorization",
                value: "Bearer {google_analytics_token}",
              },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "dateRanges",
                value: '[{"startDate":"7daysAgo","endDate":"today"}]',
              },
              {
                key: "metrics",
                value:
                  '[{"name":"sessions"},{"name":"totalUsers"},{"name":"conversions"}]',
              },
            ],
            variables: [
              {
                name: "rows[0].metricValues[0].value",
                customName: "weekly_sessions",
                type: "number",
              },
              {
                name: "rows[0].metricValues[1].value",
                customName: "weekly_users",
                type: "number",
              },
              {
                name: "rows[0].metricValues[2].value",
                customName: "weekly_conversions",
                type: "number",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI confidence >= 75?", 960, "75"),

      // TRUE path — email digest to leadership
      emailNode(
        "6",
        "SendGrid: Email weekly KPI digest to leadership",
        1200,
        "{leadership_email}",
        "Weekly Marketing KPI Digest — Week Ending {week_ending}",
        "WEEKLY MARKETING KPI DIGEST\n============================\nWeek Ending: {week_ending}\n\nEXECUTIVE SUMMARY:\n{executive_summary}\n\nKEY METRICS:\n- Leads Generated: {total_leads} ({leads_vs_target} vs target)\n- Pipeline Created: ${total_pipeline}\n- Ad Spend: ${total_ad_spend} | CPL: ${cost_per_lead}\n- Top Channel: {top_channel}\n- Website: {weekly_sessions} sessions, {weekly_users} users, {weekly_conversions} conversions\n\nSTRATEGIC RECOMMENDATIONS:\n{strategic_recommendations}\n\nRISK FLAGS:\n{risk_flags}\n\n---\nGenerated by OmniEngage AI | Confidence: {ai_confidence}% | Case: {case_id}",
      ),

      // FALSE path — marketing director review
      approvalNode(
        "7",
        "Approval: Marketing director reviews KPI narrative",
        1200,
        "Weekly KPI digest needs review before sending to leadership. AI confidence: {ai_confidence}. Summary: {executive_summary}. Risk flags: {risk_flags}. Evidence: {ai_evidence}. Please review the narrative for accuracy and approve for distribution.",
        6,
        400,
      ),

      verifyNode(
        "8",
        "Verify: Confirm digest email was delivered",
        1440,
        "GET",
        "https://api.sendgrid.com/v3/messages?query=to_email%3D{leadership_email}&limit=1",
        [{ key: "Authorization", value: "Bearer {sendgrid_key}" }],
        [
          {
            name: "messages[0].status",
            customName: "digest_delivery_status",
            type: "string",
          },
          {
            name: "messages[0].msg_id",
            customName: "digest_msg_id",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Digest delivered successfully?",
        1680,
        "verify_status",
        "EQUALS",
        "200",
      ),

      sheetsNode(
        "10",
        "Google Sheets: Log KPI digest generation audit trail",
        1920,
        "KPIDigests",
        '[["{case_id}","{week_ending}","{total_leads}","{leads_vs_target}","{total_pipeline}","{cost_per_lead}","{top_channel}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Compiles weekly marketing KPIs from Google Analytics and HubSpot, uses AI to generate executive narrative analysis, auto-emails leadership for high-confidence digests, routes uncertain narratives for marketing director review, verifies delivery, and logs the audit trail.",
  },
};
